/**
 * Centralized Service for managing function implementations
 * Solves cyclic dependency problems by providing runtime registration
 */

class CentralizedService {
  constructor() {
    this.registry = new Map();
    this.defaultImplementations = new Map();
  }

  /**
   * Register an implementation for a service function
   * @param {string} name - The service function name
   * @param {Function} implementation - The implementation function
   */
  register(name, implementation) {
    this.registry.set(name, implementation);
  }

  /**
   * Get the implementation for a service function
   * @param {string} name - The service function name
   * @returns {Function} The implementation function or default
   */
  get(name) {
    return this.registry.get(name) || this.defaultImplementations.get(name);
  }

  /**
   * Set a default implementation for a service function
   * @param {string} name - The service function name
   * @param {Function} implementation - The default implementation
   */
  setDefault(name, implementation) {
    this.defaultImplementations.set(name, implementation);
  }

  /**
   * Check if an implementation is registered
   * @param {string} name - The service function name
   * @returns {boolean} True if registered
   */
  has(name) {
    return this.registry.has(name);
  }

  /**
   * Clear a specific implementation
   * @param {string} name - The service function name
   */
  clear(name) {
    this.registry.delete(name);
  }

  /**
   * Clear all implementations
   */
  clearAll() {
    this.registry.clear();
  }
}

// Create singleton instance
const service = new CentralizedService();

// Default displayError implementation
const defaultDisplayError = (e) => {
  console.log("displayError placeholder called with:", e);
  if (typeof e === 'string') return e;
  if (e?.message) return e.message;
  return String(e);
};

// Default getBrowserTimezone implementation
const defaultGetBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Could not detect browser timezone:', error);
    return 'UTC';
  }
};

// Register default implementations
service.setDefault('displayError', defaultDisplayError);
service.setDefault('getBrowserTimezone', defaultGetBrowserTimezone);

// Export the service instance
export default service;

// Helper functions for common operations
export const registerService = (name, implementation) => service.register(name, implementation);
export const getService = (name) => service.get(name);
export const hasService = (name) => service.has(name);
export const clearService = (name) => service.clear(name);

// Export specific service functions
export const displayError = (e) => {
  const implementation = service.get('displayError');
  return implementation(e);
};

export const getBrowserTimezone = () => {
  const implementation = service.get('getBrowserTimezone');
  return implementation();
};